import useApi from 'api';
import { toggleItem } from 'helpers/utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

export function useAsyncFunction<T>(
  fn: (...args: any[]) => Promise<T>,
  callback?: () => void,
): [(...args: any[]) => Promise<T | undefined>, boolean] {
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef<boolean | null>(null);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  });

  const wrapper = async (...args: any[]) => {
    setIsLoading(true);
    try {
      const result = await fn(...args);
      if (isMounted.current) {
        setIsLoading(false);

        if (callback) {
          callback();
        }

        return result;
      }
    } catch (error) {
      if (isMounted.current) {
        setIsLoading(false);
        throw error;
      }
    }
  };

  return [wrapper, isLoading];
}

export function useAppStateLanguage(initial?: Lang): [Lang, React.Dispatch<React.SetStateAction<Lang>>] {
  const [lang, setLang] = useState(initial || window.appState.lang);

  useEffect(() => {
    window.appState.lang = lang;
  }, [lang]);

  return [lang, setLang];
}

export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();

  const [listeners, setListeners] = useState<Function[]>([]);

  useEffect(() => {
    listeners.forEach((func) => func(location.pathname));
  }, [location.pathname]);

  const listen = (func: Function) => {
    setListeners((old) => [...old, func]);

    function unregisterCallback() {
      setListeners((old) => toggleItem(old, func));
    }

    return unregisterCallback;
  };

  return {
    push: navigate,
    go: navigate,
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    listen,
    location,
  };
}

export function useCopyToClipboard() {
  const [openPopup, setOpenPopup] = useState(false);

  const copyToClipboard = (text: string) => () => {
    const textarea = document.createElement('textarea');
    textarea.innerText = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
    setOpenPopup(true);
    setTimeout(() => setOpenPopup(false), 1000);
  };

  return {
    copyToClipboard,
    openCopiedPopup: openPopup,
  };
}

export default {
  useAsyncFunction,
  useAppStateLanguage,
};
